import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { chain, filter } from 'lodash'
import { useDispatch } from 'react-redux'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import Icon from 'components/Icon'
import { useDeleteItem } from 'hooks'
import { _ } from 'utils'
import { addModal } from 'features/modals/modalSlice'
import { deleteNavigationSubMenu, updateNavigationItem } from '../graphql/mutation'
import ContextMenu from './ContextMenu'
import { CreateButton } from './NavMenu'
import NavSubMenuItem from './NavSubMenuItem'
import NavSubMenuEditor from './NavSubMenuEditor'
import NavSubMenuItemEditor from './NavSubMenuItemEditor'
import OptionButton from './OptionButton'
import {
    findMaxOrder, NavigationMenu, reorderItems, updateOrderItems
} from '.'

const S = {
    Wrapper: styled(ContextMenu.Wrapper)`
        display: flex;
        flex-direction: column;
        background: #fff;
    `,
    Label: styled(ContextMenu.Trigger)`
        white-space: nowrap;
        background: transparent;
        font-size: 16px;
        line-height: 19px;
        color: rgba(45, 48, 53, 0.75);
        margin: -8px;
        padding: 8px;
        font-weight: 600;
    `,
    HeaderWrapper: styled.div`
        display: flex;
        align-items: center;
        padding: 5px;
    `,
    SubItemLabelContainer: styled.div`
        padding-left: 15px;
        padding-right: 15px;
    `,
    MenuItemsWrapper: styled.div`
        display: flex;
        flex-direction: column;
    `,
}

type SubMenu = NavigationMenu['subMenus'][0]

interface Props {
    subMenu: SubMenu
    onDeleted: () => void
}

const NavSubMenu = ({ subMenu: initSubMenu, onDeleted, ...dragHandleProps }: Props) => {
    const dispatch = useDispatch()
    const [subMenu, setSubMenu] = useState<SubMenu>(initSubMenu)
    const [subMenuItems, setSubMenuItems] = useState<SubMenu['items']>(initSubMenu.items || [])
    const { showDeleteConfirmModal } = useDeleteItem(deleteNavigationSubMenu, onDeleted)

    const showCreateItemModal = useCallback(() => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <NavSubMenuItemEditor
                    close={close}
                    newOrder={findMaxOrder(subMenuItems) + 1}
                    submenuId={`${subMenu.id}`}
                    onSubmitted={(createdItem) => setSubMenuItems([...subMenuItems, createdItem])}
                />
            ),
            shouldNotWrap: true
        }))
    }, [dispatch, subMenu.id, subMenuItems])

    const showEditItemModal = useCallback(() => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <NavSubMenuEditor
                    close={close}
                    onSubmitted={(updatedSubMenu) => setSubMenu(updatedSubMenu)}
                    subMenu={{ id: subMenu.id, title: subMenu.text }}
                />
            ),
            shouldNotWrap: true
        }))
    }, [dispatch, subMenu.id, subMenu.text])

    const handleDelete = useCallback(() => {
        showDeleteConfirmModal(subMenu.id)
    }, [subMenu.id, showDeleteConfirmModal])

    const subMenuItemViews = useMemo(() => chain(subMenuItems)
        .sortBy('order')
        .map((item, index) => (
            <Draggable key={item.id} draggableId={`${item.id}`} index={index}>
                {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                        <NavSubMenuItem
                            item={item}
                            onDeleted={() => setSubMenuItems(filter(subMenuItems, (i) => i.id !== item.id))}
                            {...provided.dragHandleProps}
                        />
                    </div>
                )}
            </Draggable>
        ))
        .value(), [subMenuItems])

    const handleDragEnd = useCallback(({ destination, source }) => {
        reorderItems(subMenuItems, source?.index, destination?.index)

        // sync new order with server
        updateOrderItems(subMenuItems, updateNavigationItem)

        // Update list for the new orders
        setSubMenuItems([...subMenuItems])
    }, [subMenuItems])

    return (
        <S.Wrapper>
            <S.HeaderWrapper>
                <div {...dragHandleProps}>
                    <Icon icon="dragVertical" style={{ marginBottom: 2 }} />
                </div>
                <S.Label>
                    {subMenu.text}

                    <ContextMenu.Container>
                        <OptionButton icon="edit" onClick={showEditItemModal} />
                        {!subMenuItems || subMenuItems.length === 0
                            ? <OptionButton icon="viewOff" />
                            : <OptionButton icon="view" active />}
                        <OptionButton icon="trash" onClick={handleDelete} />
                    </ContextMenu.Container>
                </S.Label>
            </S.HeaderWrapper>
            <S.SubItemLabelContainer>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId={`droppable-submenu-item-${subMenu.id}`} direction="vertical">
                        {(provided) => (
                            <S.MenuItemsWrapper ref={provided.innerRef} {...provided.droppableProps}>
                                {subMenuItemViews}
                                {provided.placeholder}
                            </S.MenuItemsWrapper>
                        )}
                    </Droppable>
                </DragDropContext>
                <CreateButton onClick={showCreateItemModal} label={_('navigation.create_submenu')} />
            </S.SubItemLabelContainer>
        </S.Wrapper>
    )
}

export default NavSubMenu
